import { Injectable } from '@angular/core';
import { LocalStorageService } from 'angular-2-local-storage';
import { Apollo } from 'apollo-angular';
import { WebSocketLink } from 'apollo-link-ws';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import gql from 'graphql-tag';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { assign } from 'lodash';
import { v4 as uuid } from 'uuid';

import { User, Input } from 'api/models';

declare var _uiq: any[];

export interface Login {
  readonly otpToken: string;
  readonly url: string; // for "My Profile" and "Administration" link back to qTest
}

const userFragment = gql`
fragment userFields on User {
  qtestUserId
  username
  settings {
    notifiedVersion
  }
  firstName
  lastName
  avatar
  account {
    qtestClientId
    name
  }
  projects {
    id
    name
  }
  applications {
    name
    displayOrder
    url
    hasLicense
    hasPermission
  }
  supportLinks {
    submitTicket
  }
}
`;
const userQuery = gql`
query Me {
  me {
    ...userFields
  }
}
${userFragment}
`;
const updateMe = gql`
mutation UpdateMe($user: UserInput!) {
  updateMe(user: $user) {
    ...userFields
  }
}
${userFragment}
`;

@Injectable()
export class UserService {
  static LOGIN_KEY = 'login';

  readonly user: BehaviorSubject<User & Login | undefined> = new BehaviorSubject<User & Login | undefined>(undefined);

  private wsClient?: SubscriptionClient;
  private interval?: number;

  constructor(private apollo: Apollo, private localStorageService: LocalStorageService) {
  }

  async resume() {
    let login: Login = this.localStorageService.get(UserService.LOGIN_KEY);
    if (login) {
      return this.login(login);
    }
    throw new Error();
  }

  async login(login: Login) {
    const localStorageData: any = this.localStorageService.get(UserService.LOGIN_KEY);

    const otpToken = login.otpToken;
    const tokenKey = (otpToken && uuid()) || (localStorageData && localStorageData.tokenKey);

    // this will throw if the token is invalid
    this.wsClient = await this.subscriptionClient(otpToken, tokenKey);

    // force reconnect after 1 min to check token validity
    this.interval = window.setInterval(() => {
      if (this.wsClient) {
        // this.wsClient.tryReconnect() doesn't really close the ws connection
        this.wsClient.client.close();
      }
    }, 60000);

    (this.apollo as any).map.delete(UserService.LOGIN_KEY);
    if (this.wsClient !== undefined ) {
      this.apollo.create({
        link: new WebSocketLink(this.wsClient),
        cache: new InMemoryCache()
      }, UserService.LOGIN_KEY);
    }

    let user = (await this.apollo.use(UserService.LOGIN_KEY).query<{
      me: User;
    }>({ query: userQuery }).toPromise()).data.me;

    this.localStorageService.set(UserService.LOGIN_KEY, {
      tokenKey,
      url: login.url
    });

    user.applications.sort((firstApp, secondApp) => firstApp.displayOrder - secondApp.displayOrder);

    this.user.next(assign({}, login, user));
    this.userIq(user);
    return this.user;
  }

  logout(doCleanStorage: boolean = true) {
    if (this.wsClient) {
      this.wsClient.unsubscribeAll();
      this.wsClient.close();
    }
    if (this.interval) {
      clearInterval(this.interval);
    }
    delete this.wsClient;
    delete this.interval;
    if (doCleanStorage) {
      this.localStorageService.remove(UserService.LOGIN_KEY);
    }
    this.user.next(undefined);
  }

  async updateUser(user: Partial<Input<User>>): Promise<User | null |  undefined> {
    let u = await this.apollo.mutate<{ updateMe: User }>({
      mutation: updateMe,
      variables: {
        user
      }
    }).pipe(map(({ data }) => data ? data.updateMe : data)).toPromise();
    if (u) {
      this.user.next(assign({}, this.user.value, u));
    }
    return u;
  }

  private subscriptionClient(otpToken?: string, tokenKey?: string): Promise<SubscriptionClient | undefined> {
    return new Promise((resolve, reject) => {
      this.wsClient = new SubscriptionClient(
        `${config.ROOT_URL.replace(/^http/, 'ws')}${config.GRAPHQL_ENDPOINT}`,
        {
          reconnect: true,
          connectionParams: {
            otpToken,
            tokenKey
          },
          connectionCallback: err => {
            if (err) {
              // disconnect if there is error (login failed)
              this.logout();
              reject(err);
            } else {
              resolve(this.wsClient);
            }
          }
        }
      );
    });
  }

  private userIq(user: User) {
    if (!config.USERIQ_ID || !_uiq) {
      return;
    }
    _uiq.push(['setSiteId', config.USERIQ_ID]);
    _uiq.push(['identify', user.qtestUserId, {
      user_name: [user.firstName, user.lastName].join(' '),
      account_id: user.account.qtestClientId,
      account_name: user.account.name,
      user_email: user.username
    }]);
    _uiq.push(['startTracker']);
  }
}
